import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useChatByQuoteId } from 'src/api/db/apiChat'
import {
  AGENT_INSTRUCTION_KIND_LABELS,
  AGENT_INSTRUCTION_KINDS,
  useAgentInstructionsApi,
  type AgentInstructionKind,
} from 'src/api/db/apiAgentInstructions'
import { useInstructionDraft } from 'src/api/server/useInstructionDraft'
import {
  FormInput,
  FormSelect,
  FormTextarea,
  FormWrapper,
} from 'src/shared/components/FormComponents'
import { Column } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'

const feedbackSchema = z.object({
  feedback: z.string().min(1),
})

const reviewSchema = z.object({
  kind: z.enum(AGENT_INSTRUCTION_KINDS),
  title: z.string().min(1),
  body: z.string().min(1),
})

type FeedbackValues = z.infer<typeof feedbackSchema>
type ReviewValues = z.infer<typeof reviewSchema>

export const RequestImproveResponseDialog = ({
  open,
  onOpenChange,
  quoteId,
  messageId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  quoteId: number
  messageId?: number | null
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { chat } = useChatByQuoteId(quoteId)
  const draft = useInstructionDraft()
  const { createInstruction } = useAgentInstructionsApi({ enabled: false })
  const [step, setStep] = useState<'feedback' | 'review'>('feedback')
  const [feedbackText, setFeedbackText] = useState('')

  const feedbackForm = useForm<FeedbackValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { feedback: '' },
  })

  const reviewForm = useForm<ReviewValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: { kind: 'agent', title: '', body: '' },
  })

  useEffect(() => {
    if (!open) {
      setStep('feedback')
      setFeedbackText('')
      feedbackForm.reset({ feedback: '' })
      reviewForm.reset({ kind: 'agent', title: '', body: '' })
    }
  }, [open])

  const kindOptions = AGENT_INSTRUCTION_KINDS.map((kind) => ({
    value: kind,
    label: t(
      AGENT_INSTRUCTION_KIND_LABELS[kind].en,
      AGENT_INSTRUCTION_KIND_LABELS[kind].sv
    ),
  }))

  const onDraft = async (values: FeedbackValues) => {
    if (!chat?.id) {
      toast({
        variant: 'destructive',
        title: t('No chat found', 'Ingen chatt hittades'),
      })
      return
    }

    const result = await draft.mutateAsync({
      chat_id: chat.id,
      message_id: messageId,
      feedback: values.feedback,
    })
    setFeedbackText(values.feedback)
    reviewForm.reset({
      kind: result.kind,
      title: result.title,
      body: result.body,
    })
    setStep('review')
  }

  const onSave = async (values: ReviewValues) => {
    if (!chat?.id) return
    await createInstruction.mutateAsync({
      kind: values.kind,
      title: values.title,
      body: values.body,
      source_chat_id: chat.id,
      source_message_id: messageId ?? null,
      source_feedback: feedbackText,
      is_active: true,
    })
    toast({
      variant: 'success',
      title: t('Instruction saved', 'Instruktionen sparad'),
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 'feedback'
              ? t('Improve this response', 'Förbättra svaret')
              : t('Review instruction', 'Granska instruktion')}
          </DialogTitle>
        </DialogHeader>

        {step === 'feedback' ? (
          <FormWrapper methods={feedbackForm} onSubmit={onDraft}>
            <FormTextarea
              name="feedback"
              methods={feedbackForm}
              rows={5}
              label={t('What was wrong?', 'Vad var fel?')}
              placeholder={t(
                'e.g. Do not recommend cycling jerseys for office onboarding kits',
                't.ex. Rekommendera inte cykeltröjor till onboardingkit på kontor'
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                {t('Cancel', 'Avbryt')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={draft.isPending}
                disabled={draft.isPending || !chat?.id}
              >
                {t('Draft instruction', 'Skapa utkast')}
              </Button>
            </DialogFooter>
          </FormWrapper>
        ) : (
          <FormWrapper methods={reviewForm} onSubmit={onSave}>
            <Column className="gap-3">
              <FormSelect
                name="kind"
                methods={reviewForm}
                label={t('Applies to', 'Gäller')}
                options={kindOptions}
              />
              <FormInput
                name="title"
                methods={reviewForm}
                label={t('Title', 'Titel')}
              />
              <FormTextarea
                name="body"
                methods={reviewForm}
                rows={8}
                label={t('Instruction', 'Instruktion')}
              />
              <Link
                to={paths.adminInstructions}
                className="text-xs text-muted-foreground underline"
              >
                {t('View all instructions', 'Visa alla instruktioner')}
              </Link>
            </Column>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep('feedback')}
                disabled={createInstruction.isPending}
              >
                {t('Back', 'Tillbaka')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={createInstruction.isPending}
                disabled={createInstruction.isPending}
              >
                {t('Save instruction', 'Spara instruktion')}
              </Button>
            </DialogFooter>
          </FormWrapper>
        )}
      </DialogContent>
    </Dialog>
  )
}
